import type { AccessList, AccessPlayer, AccessStatus, Invite, InvitePreview, MeResponse, TeamDetail, TeamRole, TeamSummary } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";

export const keys = {
  me: ["me"] as const,
  teams: ["teams"] as const,
  team: (id: string) => ["teams", id] as const,
  invite: (code: string) => ["invite", code] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api<MeResponse>("GET", "/me"), staleTime: 60_000 });

export const useTeams = (enabled = true) => useQuery({ queryKey: keys.teams, queryFn: () => api<TeamSummary[]>("GET", "/teams"), enabled });

export const useTeam = (id: string | undefined) => useQuery({ queryKey: keys.team(id ?? ""), queryFn: () => api<TeamDetail>("GET", `/teams/${id}`), enabled: Boolean(id) });

export const useInvitePreview = (code: string) => useQuery({ queryKey: keys.invite(code), queryFn: () => api<InvitePreview>("GET", `/invites/${code}`), retry: false });

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<TeamDetail>("POST", "/teams", { name }),
    onSuccess: (team) => {
      qc.setQueryData(keys.team(team.id), team);
      return qc.invalidateQueries({ queryKey: keys.teams });
    },
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api<TeamSummary>("POST", `/invites/${code}/accept`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.teams }),
  });
}

export function useRotateInvite(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Invite>("POST", `/teams/${teamId}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.team(teamId) }),
  });
}

export function useSetRole(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) => api<TeamDetail>("PATCH", `/teams/${teamId}/members/${userId}`, { role }),
    onSuccess: (team) => {
      qc.setQueryData(keys.team(teamId), team);
      return qc.invalidateQueries({ queryKey: keys.teams });
    },
  });
}

export function useRemoveMember(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<void>("DELETE", `/teams/${teamId}/members/${userId}`),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: keys.team(teamId) }), qc.invalidateQueries({ queryKey: keys.teams })]),
  });
}

export function useLeaveTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("POST", `/teams/${teamId}/leave`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.team(teamId) });
      return qc.invalidateQueries({ queryKey: keys.teams });
    },
  });
}

/** Renames the team (owner and captains); 0.23.0. */
export function useRenameTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<TeamDetail>("PATCH", `/teams/${teamId}`, { name }),
    onSuccess: (team) => {
      qc.setQueryData(keys.team(teamId), team);
      return qc.invalidateQueries({ queryKey: keys.teams });
    },
  });
}

/** Deletes the team and everything in it (the owner only); 0.23.0. */
export function useDeleteTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", `/teams/${teamId}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.team(teamId) });
      return qc.invalidateQueries({ queryKey: keys.teams });
    },
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("POST", "/auth/logout"),
    onSuccess: () => qc.clear(),
  });
}

export function useDevSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<{ user: MeResponse["user"] }>("POST", "/auth/dev", { name }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

// ---- Access (admins only) ----

export const accessKey = ["admin", "access"] as const;

export const useAccessList = () => useQuery({ queryKey: accessKey, queryFn: () => api<AccessList>("GET", "/admin/access") });

export function useSetAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, access }: { userId: string; access: AccessStatus }) => api<AccessPlayer>("PUT", `/admin/access/${userId}`, { access }),
    onSuccess: () => qc.invalidateQueries({ queryKey: accessKey }),
  });
}

export function useAddPlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { steam: string; name?: string }) => api<AccessPlayer>("POST", "/admin/access", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: accessKey }),
  });
}
